import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import axios from "axios";

const SHOPIFY_URL = "https://parfumalgerie.shop";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const collections = [
  { id: "all", title: "Tous", handle: "all" },
  { id: "homme", title: "Homme", handle: "homme" },
  { id: "femme", title: "Femme", handle: "femme" },
  { id: "best-sellers", title: "Best-Sellers", handle: "best-sellers" },
  { id: "popular", title: "Populaires", handle: "popular" },
];

const ProductCard = ({ product, index }) => {
  return (
    <motion.a
      href={product.product_url}
      target="_blank"
      rel="noopener noreferrer"
      layout
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        duration: 0.5,
        delay: index * 0.05,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="product-card block group cursor-pointer"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative bg-white overflow-hidden aspect-[3/4]">
        {/* Image */}
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.title}
            className="product-image w-full h-full object-cover bg-[#F5F5F5]"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-[#F5F5F5] flex items-center justify-center">
            <span className="text-gray-400 font-montserrat text-sm">No image</span>
          </div>
        )}

        {/* Overlay on Hover */}
        <div className="overlay absolute inset-0 bg-black/30 flex items-center justify-center">
          <span className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] px-6 py-3 rounded-full font-montserrat text-xs uppercase tracking-[0.15em]">
            Voir le produit
            <ArrowUpRight size={14} />
          </span>
        </div>

        {/* Category Badge */}
        {product.category && (
          <div className="absolute top-4 left-4">
            <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full font-montserrat text-[10px] uppercase tracking-[0.15em] text-[#666]">
              {product.category}
            </span>
          </div>
        )}

        {/* Sale badge */}
        {product.compare_at_price && (
          <div className="absolute top-4 right-4">
            <span className="bg-[#D4AF37] text-white px-3 py-1 rounded-full font-montserrat text-[10px] uppercase tracking-[0.15em]">
              Promo
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 bg-white">
        <h3 className="font-playfair text-base md:text-lg text-[#1A1A1A] mb-1 group-hover:text-[#D4AF37] transition-colors line-clamp-2 min-h-[3rem]">
          {product.title}
        </h3>
        <div className="flex items-center gap-2">
          <p className="font-montserrat text-sm text-[#1A1A1A] tracking-wide font-medium">
            {product.price}
          </p>
          {product.compare_at_price && (
            <p className="font-montserrat text-xs text-[#999] tracking-wide line-through">
              {product.compare_at_price}
            </p>
          )}
        </div>
      </div>
    </motion.a>
  );
};

const CollectionTabs = ({ activeCollection, onCollectionChange }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
      {collections.map((collection) => (
        <button
          key={collection.id}
          onClick={() => onCollectionChange(collection.handle)}
          className={`px-5 py-2.5 rounded-full font-montserrat text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
            activeCollection === collection.handle
              ? "bg-[#1A1A1A] text-white"
              : "bg-white text-[#1A1A1A] border border-black/10 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          }`}
          data-testid={`collection-tab-${collection.handle}`}
        >
          {collection.title}
        </button>
      ))}
    </div>
  );
};

const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCollection, setActiveCollection] = useState("best-sellers");

  const fetchProducts = async (collection) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`, {
        params: { limit: 12, collection }
      });
      if (response.data.products) {
        setProducts(response.data.products);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(activeCollection);
  }, [activeCollection]);

  const handleCollectionChange = (collection) => {
    setActiveCollection(collection);
  };

  return (
    <section id="products" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="font-montserrat text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4 block">
            Notre Collection
          </span>
          <h2 className="font-playfair text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-6">
            Parfums <span className="italic font-cormorant">d'Exception</span>
          </h2>
          <p className="font-montserrat text-base text-[#666] max-w-2xl mx-auto leading-relaxed">
            Explorez notre sélection de fragrances pour homme, femme et unisexe.
            Des parfums authentiques livrés partout en Algérie.
          </p>
        </motion.div>

        {/* Collection Tabs */}
        <CollectionTabs
          activeCollection={activeCollection}
          onCollectionChange={handleCollectionChange}
        />

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
            <span className="ml-3 font-montserrat text-[#666]">Chargement...</span>
          </div>
        )}

        {/* Products Grid */}
        {!loading && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCollection}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            >
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <div className="text-center py-16">
            <p className="font-montserrat text-[#666]">Aucun produit trouvé dans cette collection.</p>
          </div>
        )}

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12 md:mt-16"
        >
          <a
            href={SHOPIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-3"
            data-testid="view-all-products-btn"
          >
            Voir Toute la Collection
            <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;
